// The cue card, full screen (04 §2 → `/leads/:id/cue`).
//
// This is the only screen in the app designed to be read while something else is happening. It
// gets its own route with no chrome, large type, and nothing that can be tapped by accident —
// the owner is holding a phone, not browsing.
//
// What it will not do: show a name or a number the pipeline could not confirm. Those fields
// arrive already blanked by the renderer, and the card says so out loud rather than leaving a
// gap the owner might fill in from memory.

import { Link, useParams } from 'react-router-dom';
import { useCueCard } from './useDossier.js';
import './cuecard.css';

export function CueCardPage() {
  const { id } = useParams();
  const leadId = id === undefined ? null : Number(id);
  const { data, isLoading, error } = useCueCard(leadId);

  if (isLoading) return <div className="cue cue--msg">Зареждане на картонче за разговор…</div>;
  if (error) return <div className="cue cue--msg">{(error as Error).message}</div>;
  if (!data) {
    return (
      <div className="cue cue--msg">
        <p>Няма генерирано картонче за този обект.</p>
        <Link to={`/leads/${id ?? ''}`} className="cue__back-btn">
          ← Обратно към досието
        </Link>
      </div>
    );
  }

  const card = data.content;

  return (
    <div className="cue">
      <header className="cue__top">
        <Link className="cue__back" to={`/leads/${id ?? ''}`}>
          ← Назад към досието
        </Link>
        <div className="cue__top-badges">
          {card.priority ? <span className="cue__priority">{card.priority}</span> : null}
          {card.dead_hours ? (
            <span className="cue__dead">Мъртви часове: {card.dead_hours}</span>
          ) : null}
        </div>
      </header>

      {/* Large Dial Button - Principal Action */}
      {card.phone ? (
        <a className="cue__dial" href={`tel:${card.phone}`}>
          <span className="cue__dial-icon">📞</span>
          <span className="cue__dial-num">{card.phone}</span>
          <span className="cue__dial-hint">Натиснете за обаждане</span>
        </a>
      ) : (
        <div className="cue__nodial">
          <span className="cue__nodial-icon">⚠️</span>
          <span>Няма потвърден телефонен номер — проверете сайта или регистъра в досието.</span>
        </div>
      )}

      {/* Opening Hook Pitch Card */}
      <section className="cue__section cue__section--open">
        <span className="cue__sec-label">Първо изречение към собственика</span>
        <p className="cue__open">{card.owner_line}</p>
        <p className="cue__why">{card.why_one_liner}</p>
      </section>

      {/* Paired Findings */}
      <section className="cue__section">
        <span className="cue__sec-label">Какво открихме и какво им струва</span>
        <ol className="cue__pairs">
          {card.top_pairs.map((pair, idx) => (
            <li key={pair.check_key} className="cue__pair">
              <span className="cue__pair-num">0{idx + 1}</span>
              <div className="cue__pair-content">
                <span className="cue__seen">{pair.seen}</span>
                <span className="cue__costs">→ {pair.costs}</span>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Do NOT say block */}
      {card.never_say.length > 0 ? (
        <section className="cue__never">
          <div className="cue__never-head">
            <span className="cue__never-icon">⛔</span>
            <h2>Не казвайте в никакъв случай</h2>
          </div>
          <ul className="cue__never-list">
            {card.never_say.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Next Action Call To Action */}
      <section className="cue__next-card">
        <span className="cue__sec-label">Цел на разговора и следваща стъпка</span>
        <p className="cue__next">{card.next_action}</p>
      </section>

      {data.model === null ? (
        <div className="cue__flag">
          <span>
            ⚠️ Текстът е машинен шаблон
            {data.status === 'needs_review'
              ? ' — генерираният AI вариант беше задържан за проверка.'
              : data.llm_error
                ? ` — моделът отказа: ${data.llm_error.slice(0, 120)}.`
                : '.'}
          </span>
        </div>
      ) : null}
    </div>
  );
}
